import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Pencil, Car } from "lucide-react";
import { CustomerProfileFields, customerProfilePayload } from "@/components/CustomerProfileFields";
import { toast } from "sonner";
import { PlateCameraButton } from "@/components/PlateCameraButton";

const lbl = "text-xs font-mono-caps text-zinc-400";
const inp = "mt-1 bg-zinc-900 border-zinc-800";

const toVehicleForm = (v) => ({
  original_plate: v.plate,
  make: v.make || "", model: v.model || "", year: v.year ?? "", year_model: v.year_model ?? "",
  plate: v.plate || "", color: v.color || "", current_km: v.current_km ?? "", chassi: v.chassi || "",
});

const sameVehicle = (a, b) =>
  ["make", "model", "year", "year_model", "plate", "color", "current_km", "chassi"].every((k) => String(a[k] ?? "") === String(b[k] ?? ""));

function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label className={lbl}>{label}</Label>
      <Input data-testid={testid} className={inp} {...props} />
    </div>
  );
}

function VehicleFields({ v, idx, onChange }) {
  const set = (k) => (e) => onChange(idx, k, e.target.value);
  return (
    <div data-testid={`edit-vehicle-${idx}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-bold text-zinc-200">
        <Car className="h-4 w-4 text-green-brand" /> {v.original_plate}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={lbl}>Placa</Label>
          <div className="flex gap-2">
            <Input data-testid={`edit-vehicle-plate-${idx}`} className={inp} required value={v.plate} onChange={(e) => onChange(idx, "plate", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7))} />
            <PlateCameraButton testid={`edit-vehicle-plate-camera-${idx}`} onRead={(p) => onChange(idx, "plate", p)} />
          </div>
        </div>
        <Field label="Marca" testid={`edit-vehicle-make-${idx}`} required value={v.make} onChange={set("make")} />
        <Field label="Modelo" testid={`edit-vehicle-model-${idx}`} required value={v.model} onChange={set("model")} />
        <Field label="Ano fabricação" testid={`edit-vehicle-year-${idx}`} type="number" value={v.year} onChange={set("year")} />
        <Field label="Ano modelo" testid={`edit-vehicle-year-model-${idx}`} type="number" value={v.year_model} onChange={set("year_model")} />
        <Field label="Cor" testid={`edit-vehicle-color-${idx}`} value={v.color} onChange={set("color")} />
        <Field label="Quilometragem" testid={`edit-vehicle-km-${idx}`} type="number" min={0} value={v.current_km} onChange={set("current_km")} />
        <div className="col-span-2">
          <Field label="Chassi (opcional)" testid={`edit-vehicle-chassi-${idx}`} maxLength={17} value={v.chassi} onChange={(e) => onChange(idx, "chassi", e.target.value.toUpperCase())} />
        </div>
      </div>
    </div>
  );
}

export function EditCustomerDialog({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [profile, setProfile] = useState({ person_type: "pf" });
  const [vehicles, setVehicles] = useState([]);
  const [initial, setInitial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!customer) { return; }
    setForm({ name: customer.name || "", email: customer.email || "", phone: customer.phone || "" });
    setProfile({
      person_type: customer.person_type || "pf",
      document: customer.document || "",
      legal_name: customer.legal_name || "",
      trading_name: customer.trading_name || "",
      birth_date: customer.birth_date || "",
      cep: customer.cep || "",
      street: customer.street || "",
      number: customer.number || "",
      complement: customer.complement || "",
      neighborhood: customer.neighborhood || "",
      city: customer.city || "",
      uf: customer.uf || "",
    });
    setLoading(true);
    api.get(`/customers/${customer.id}/vehicles`)
      .then(({ data }) => { const f = data.map(toVehicleForm); setVehicles(f); setInitial(f); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [customer]);

  const changeVehicle = (idx, k, val) => setVehicles((p) => p.map((v, i) => (i === idx ? { ...v, [k]: val } : v)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    let data = null;
    try {
      ({ data } = await api.patch(`/customers/${customer.id}`, { ...form, ...customerProfilePayload(profile) }));
      for (let i = 0; i < vehicles.length; i++) {
        const v = vehicles[i];
        if (sameVehicle(v, initial[i])) continue;
        await api.patch(`/customers/${customer.id}/vehicles`, {
          original_plate: v.original_plate,
          vehicle: {
            make: v.make, model: v.model, plate: v.plate, color: v.color || null, chassi: v.chassi || null,
            year: v.year === "" ? null : Number(v.year),
            year_model: v.year_model === "" ? null : Number(v.year_model),
            current_km: v.current_km === "" ? null : Number(v.current_km),
          },
        });
      }
      const emailChanged = data.email !== (customer.email || "").toLowerCase();
      toast.success(emailChanged ? `Cadastro salvo. O login agora é ${data.email}.` : "Cadastro do cliente atualizado");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
      if (data) onSaved(data, true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!customer} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-2">
            <Pencil className="h-5 w-5 text-green-brand" /> Editar cliente
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Se você mudar o e-mail, o cliente passa a entrar com o novo e-mail (a senha continua a mesma).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="edit-customer-form">
          <Field label="Nome completo" testid="edit-customer-name" required value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <Field label="E-mail (login)" testid="edit-customer-email" type="email" required value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          <Field label="Telefone" testid="edit-customer-phone" value={form.phone}
            onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          <details data-testid="edit-customer-profile" className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
            <summary className="cursor-pointer text-xs font-mono-caps text-zinc-400 uppercase tracking-wider">Dados completos (CPF/CNPJ, endereço…)</summary>
            <div className="mt-3">
              <CustomerProfileFields value={profile} onChange={setProfile} idPrefix="edit-customer" />
            </div>
          </details>
          <div className="pt-2">
            <div className="text-xs font-mono-caps text-zinc-400 mb-2">Veículos</div>
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-green-brand" />
            ) : vehicles.length === 0 ? (
              <p data-testid="edit-customer-no-vehicles" className="text-sm text-zinc-500">
                Nenhum veículo ainda. Os veículos aparecem aqui depois da primeira ordem de serviço.
              </p>
            ) : (
              <div className="space-y-3">
                {vehicles.map((v, i) => <VehicleFields key={v.original_plate} v={v} idx={i} onChange={changeVehicle} />)}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={saving} onClick={onClose} className="text-zinc-300 hover:bg-zinc-900">
              Cancelar
            </Button>
            <Button data-testid="edit-customer-submit" type="submit" disabled={saving || loading}
              className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
